import React from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { LogOut } from "lucide-react";
import OfflineIndicator from "./OfflineIndicator";
import SchoolLogo from "./SchoolLogo";

export function BrandBlock({ compact = false }) {
  return (
    <Link to="/" className="flex items-center gap-3" data-testid="brand-link">
      <SchoolLogo size={compact ? 36 : 44} />
      <div className="leading-tight">
        <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#05126E]">
          Attendance System
        </div>
        <div className="text-sm sm:text-base font-extrabold tracking-tight text-[#0f1a14]">
          GANAP INC.
        </div>
      </div>
    </Link>
  );
}

export function Shell({ nav, children }) {
  const { user, logout } = useAuth();
  const { pathname } = useLocation();

  return (
    <div className="min-h-screen bg-[#F4F6F5] flex flex-col">
      <header className="sticky top-0 z-40 bg-white border-b border-[#E3E6E4] shadow-sm">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-3 flex items-center justify-between gap-3">
          <BrandBlock compact />

          <div className="hidden md:flex items-center gap-1 ml-6 flex-1 justify-end mr-4">
            {nav.map((n) => {
              const active =
                pathname === n.to || (n.to !== "/" && pathname.startsWith(n.to));
              return (
                <Link
                  key={n.to}
                  to={n.to}
                  className={`px-3 py-1.5 rounded-md text-sm font-semibold transition-colors ${
                    active
                      ? "bg-[#05126E] text-white"
                      : "text-[#0f1a14] hover:bg-[#E6E9F5]"
                  }`}
                  data-testid={`nav-${n.key}`}
                >
                  {n.label}
                </Link>
              );
            })}
          </div>

          <div className="flex items-center gap-3">
            <div className="hidden sm:block">
              <OfflineIndicator />
            </div>
            <div className="hidden sm:block text-right leading-tight">
              <div className="text-xs font-bold text-[#0f1a14]">{user?.name}</div>
              <div className="text-[10px] uppercase tracking-wider text-[#5a6663]">
                {user?.role?.replace("_", " ")}
              </div>
            </div>
            <button
              onClick={logout}
              className="p-2 rounded-md border border-[#E3E6E4] text-[#0f1a14] hover:bg-[#0f1a14] hover:text-white transition-colors"
              data-testid="logout-btn"
              aria-label="Logout"
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Mobile nav */}
        <nav className="md:hidden border-t border-[#E3E6E4] overflow-x-auto no-scrollbar bg-white">
          <div className="flex gap-0 px-2">
            {nav.map((n) => {
              const active =
                pathname === n.to || (n.to !== "/" && pathname.startsWith(n.to));
              return (
                <Link
                  key={n.to}
                  to={n.to}
                  className={`px-4 py-2 text-xs font-bold uppercase tracking-wider whitespace-nowrap border-b-2 ${
                    active
                      ? "border-[#05126E] text-[#05126E]"
                      : "border-transparent text-[#5a6663]"
                  }`}
                  data-testid={`mnav-${n.key}`}
                >
                  {n.label}
                </Link>
              );
            })}
          </div>
        </nav>

        {/* Mobile offline indicator row */}
        <div className="sm:hidden border-t border-[#E3E6E4] bg-[#F4F6F5] px-4 py-2 flex justify-between items-center">
          <OfflineIndicator />
          <span className="text-[10px] uppercase tracking-wider text-[#5a6663]">
            {user?.role?.replace("_", " ")}
          </span>
        </div>
      </header>

      <main className="flex-1 max-w-7xl w-full mx-auto p-4 md:p-8">{children}</main>

      <footer className="border-t border-[#E3E6E4] bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-4 text-xs text-[#5a6663] flex flex-col sm:flex-row gap-2 justify-between items-start sm:items-center">
          <div>
            <div className="font-semibold text-[#0f1a14]">GANAP INC.</div>
            <div>Attendance System</div>
          </div>
          <span className="mono">v1.0</span>
        </div>
      </footer>
    </div>
  );
}

export function PageHeader({ title, eyebrow, actions }) {
  return (
    <div className="flex flex-wrap items-end justify-between gap-3 mb-6 pb-4 border-b border-[#E3E6E4]">
      <div>
        {eyebrow && (
          <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#05126E] mb-2">
            {eyebrow}
          </div>
        )}
        <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-[#0f1a14]">
          {title}
        </h1>
      </div>
      {actions && <div className="flex gap-2">{actions}</div>}
    </div>
  );
}

export function StatCard({ label, value, accent }) {
  return (
    <div
      className={`brutal-card bg-white border border-[#E3E6E4] p-5 rounded-md ${
        accent ? "ring-1 ring-[#05126E]/15" : ""
      }`}
      data-testid={`stat-${label.toLowerCase().replace(/\s+/g, "-")}`}
    >
      <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#5a6663]">
        {label}
      </div>
      <div className={`mt-2 text-3xl sm:text-4xl font-extrabold tracking-tight ${accent ? "text-[#05126E]" : "text-[#0f1a14]"}`}>
        {value}
      </div>
    </div>
  );
}
